import { reactive } from 'vue'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  getQualificationDetails,
  getQualificationErrorCode,
  getQualificationMissing
} from '@/utils/qualificationErrors'
import { QualificationErrorCode } from '@/types/qualification-eligibility.types'
import type { EligibilityReason } from '@/types/qualification-eligibility.types'

export interface QualificationDialogState {
  open: boolean
  doctorName: string | null
  /** `missing` do topo do 422 (já mesclado pelo backend) ou a união dos `details[].missing`. */
  missing: EligibilityReason[]
  /**
   * Plantões barrados no lote tudo-ou-nada (`details[]`, A3.7): conta `stationId` ou, na criação
   * recorrente (plantão ainda sem id), a `stationDate` da ocorrência. 0 = backend sem `details`.
   */
  affectedCount: number
}

export interface QualificationGuardOptions {
  doctorName?: string | null
  /** Refaz a mesma gravação com `preAllocate: true` (D24/D37). */
  retryWithPreAllocate: () => unknown
}

const closedState = (): QualificationDialogState => ({
  open: false,
  doctorName: null,
  missing: [],
  affectedCount: 0
})

function countAffected(error: unknown): number {
  const keys = getQualificationDetails(error).map(
    (detail) => detail.stationId ?? detail.stationDate
  )
  return new Set(keys.filter((key) => key !== null)).size
}

/**
 * 422 de qualificação nos pontos de alocação (SPEC §4): `QUALIFICATION_NOT_MET` abre o bloqueio
 * (D15, sem liberação manual; o backend já faz o BLOCK vencer) e `QUALIFICATION_PRE_ALLOCATION_REQUIRED`
 * pede confirmação e só então reenvia **uma vez** com `preAllocate: true`. Mesmo molde do
 * `usePlaceholderDoctorBlocked`: estado por chamada e `handleError` devolve `false` para o
 * chamador seguir com o tratamento atual. Flag desligada: devolve sempre `false` (o fluxo atual
 * mostra a mensagem do backend e nenhum campo novo é enviado — SPEC §10).
 */
export function useQualificationAllocationGuard() {
  const blocked = reactive<QualificationDialogState>(closedState())
  const preAllocation = reactive<QualificationDialogState>(closedState())
  let retry: (() => unknown) | null = null

  function handleError(error: unknown, options: QualificationGuardOptions): boolean {
    if (!useFeatureFlags().flags.enabledProfessionalDocuments) return false
    const code = getQualificationErrorCode(error)
    const isBlocked = code === QualificationErrorCode.QUALIFICATION_NOT_MET
    if (!isBlocked && code !== QualificationErrorCode.QUALIFICATION_PRE_ALLOCATION_REQUIRED) {
      return false
    }
    const state: QualificationDialogState = {
      open: true,
      doctorName: options.doctorName ?? null,
      missing: getQualificationMissing(error),
      affectedCount: countAffected(error)
    }
    if (isBlocked) {
      Object.assign(blocked, state)
      Object.assign(preAllocation, closedState())
      retry = null
    } else {
      Object.assign(preAllocation, state)
      retry = options.retryWithPreAllocate
    }
    return true
  }

  /** Fecha o diálogo e reenvia; um segundo clique (ou confirmar depois de cancelar) não faz nada. */
  async function confirmPreAllocation(): Promise<void> {
    const run = retry
    retry = null
    preAllocation.open = false
    if (run) await run()
  }

  function cancelPreAllocation(): void {
    retry = null
    preAllocation.open = false
  }

  return { blocked, preAllocation, handleError, confirmPreAllocation, cancelPreAllocation }
}
